import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

    const DebugContext = createContext({});

    export function DebugProvider({ children }) {
      // PERMANENTLY ENABLED FOR TESTING
      const [debugEnabled, setDebugEnabled] = useState(true);
      const [logs, setLogs] = useState([]);

      const addLog = useCallback((level, args, type = 'console') => {
        const entry = {
          id: Date.now() + Math.random(),
          level, // 'log', 'debug', 'info', 'warn', 'error'
          type, // 'console' or 'network'
          timestamp: new Date().toISOString(),
          message: args.map(a => {
            try {
              if (a instanceof Error) return `[EXCEPTION] ${a.name}: ${a.message}\n${a.stack}`;
              return typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a);
            } catch {
              return String(a);
            }
          }).join(' '),
        };
        setLogs(prev => [entry, ...prev].slice(0, 1000));
      }, []);

      useEffect(() => {
        const handleKeyDown = (e) => {
          if (e.ctrlKey && e.shiftKey && e.key === 'D') {
            e.preventDefault();
            setDebugEnabled(prev => !prev);
          }
        };

        window.addEventListener('keydown', handleKeyDown);

        if (debugEnabled) {
          // 1. Intercept Console
          const orig = {
            log: console.log,
            warn: console.warn,
            error: console.error,
            info: console.info,
          };

          console.log = (...a) => { orig.log(...a); addLog('log', a); };
          console.warn = (...a) => { orig.warn(...a); addLog('warn', a); };
          console.error = (...a) => { orig.error(...a); addLog('error', a); };
          console.info = (...a) => { orig.info(...a); addLog('info', a); };

          // 2. Deep Network Interception
          const { fetch: originalFetch } = window;
          window.fetch = async (...args) => {
            const [url, config] = args;
            const method = config?.method || 'GET';
            const startTime = Date.now();

            if (typeof url === 'string' && (url.includes('supabase') || url.includes('api'))) {
              const shortUrl = url.split('?')[0].split('/').slice(-2).join('/');
              addLog('debug', [`>>> [REQ] ${method} ${shortUrl}`], 'network');
            }

            try {
              const response = await originalFetch(...args);
              const duration = Date.now() - startTime;
              const status = response.status;

              if (typeof url === 'string' && (url.includes('supabase') || url.includes('api'))) {
                const shortUrl = url.split('?')[0].split('/').slice(-2).join('/');
                const level = status >= 400 ? 'error' : 'info';
                
                let errorDetails = '';
                if (status >= 400) {
                  try {
                    const clone = response.clone();
                    const body = await clone.json();
                    errorDetails = `\n[BODY]: ${JSON.stringify(body, null, 2)}`;
                  } catch {
                    errorDetails = '\n[BODY]: Could not parse response';
                  }
                }
                
                addLog(level, [`<<< [${status}] ${method} ${shortUrl} (${duration}ms)${errorDetails}`], 'network');
              }
              return response;
            } catch (error) {
              addLog('error', [`!!! [NETWORK_FAILURE] ${method} ${url}: ${error.message}\n${error.stack}`], 'network');
              throw error;
            }
          };

          return () => {
            console.log = orig.log;
            console.warn = orig.warn;
            console.error = orig.error;
            console.info = orig.info;
            window.fetch = originalFetch;
            window.removeEventListener('keydown', handleKeyDown);
          };
        }

        return () => window.removeEventListener('keydown', handleKeyDown);
      }, [debugEnabled, addLog]);

      return (
        <DebugContext.Provider value={{ debugEnabled, toggleDebug: setDebugEnabled, logs, clearLogs: () => setLogs([]) }}>
          {children}
        </DebugContext.Provider>
      );
    }

    export const useDebug = () => useContext(DebugContext);